/**
 * AuthContext — single source of truth for "who is logged in" across
 * the React tree. Hydrates on first mount by asking amazon-cognito-
 * identity-js if there's a valid session in browser storage; that
 * avoids bouncing the user to /login on every page reload.
 *
 * When hydration produces no user we also remember *why* (session
 * expired vs. couldn't reach Cognito) so the Login page can say
 * "Your session expired — sign in again" instead of silently
 * presenting a blank form, and offer a retry when it was just the
 * network.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  AuthUser,
  SignInResult,
  confirmPasswordReset,
  getSessionState,
  requestPasswordReset,
  resendConfirmationCode,
  signIn as doSignIn,
  signOut as doSignOut,
} from './auth';
import { describeAuthError } from './authErrors';

export interface SessionNotice {
  kind: 'expired' | 'offline';
  title: string;
  detail: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  /**
   * Why the most recent hydration produced no user, if it was for a
   * reason worth telling the player. Cleared on sign-in and sign-out.
   */
  sessionNotice: SessionNotice | null;
  /**
   * Attempt sign-in. Returns the SignInResult so the caller can
   * distinguish a direct success from a newPasswordRequired challenge.
   * On a direct success the context's user is updated automatically.
   */
  signIn: (username: string, password: string) => Promise<SignInResult>;
  /** Re-run hydration from the browser's Cognito session (also the "retry" for an offline notice). */
  refresh: () => Promise<void>;
  signOut: () => void;
  /** Resolves with the username form Cognito accepted — hand it to confirmPasswordReset. */
  requestPasswordReset: (email: string) => Promise<string>;
  confirmPasswordReset: (username: string, code: string, newPassword: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionNotice, setSessionNotice] = useState<SessionNotice | null>(null);

  const hydrate = useCallback(async (): Promise<boolean> => {
    const state = await getSessionState();
    switch (state.status) {
      case 'signedIn':
        setUser(state.user);
        setSessionNotice(null);
        return true;
      case 'none':
        setUser(null);
        return false;
      case 'expired': {
        const info = describeAuthError(state.error, 'session');
        setUser(null);
        setSessionNotice({ kind: 'expired', title: info.title, detail: info.detail });
        return false;
      }
      case 'offline':
        setUser(null);
        setSessionNotice({
          kind: 'offline',
          title: "Couldn't check your session",
          detail:
            'The sign-in service could not be reached, so we could not confirm you are still signed in. ' +
            'Check your connection and retry — you should not need your password.',
        });
        return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    hydrate().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [hydrate]);

  // A game-night tab often sits in the background for hours. When it
  // comes back, re-check the cached session: if the refresh token has
  // been rejected meanwhile, surface "session expired" now rather than
  // letting every WebSocket reconnect fail with an opaque 401. A network
  // hiccup (offline) deliberately leaves the current user in place.
  useEffect(() => {
    if (!user) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      getSessionState().then((state) => {
        if (state.status === 'expired') {
          const info = describeAuthError(state.error, 'session');
          setUser(null);
          setSessionNotice({ kind: 'expired', title: info.title, detail: info.detail });
        } else if (state.status === 'none') {
          // Signed out from another tab.
          setUser(null);
        }
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user]);

  const signIn = useCallback(
    async (username: string, password: string): Promise<SignInResult> => {
      const result = await doSignIn(username, password);
      if (result.kind === 'success') {
        setUser(result.user);
        setSessionNotice(null);
      }
      return result;
    },
    [],
  );

  const refresh = useCallback(async () => {
    await hydrate();
  }, [hydrate]);

  const signOut = useCallback(() => {
    doSignOut();
    setUser(null);
    setSessionNotice(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      sessionNotice,
      signIn,
      refresh,
      signOut,
      requestPasswordReset,
      confirmPasswordReset,
      resendConfirmationCode,
    }),
    [user, loading, sessionNotice, signIn, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
